export const CROSS_FILTER_ID_SEPARATOR = '__x__';

/**
 * Generates a combined identifier string from an entity type and an ID.
 * To be used as unique filter identifier in cross-entity filters.
 *
 * @param {string} id - The unique identifier of the object.
 * @param {string} entity - The type of the object or entity category.
 * @returns {string} A combined string that incorporates the entity type, a separator, and the ID.
 * Example: For entity = "User" and id = "123" with a separator of "_", this returns "User_123".
 */
export const getCrossID = (id, entity) => !!entity ? `${entity}${CROSS_FILTER_ID_SEPARATOR}${id}` : id;

/**
 * Extracts the original ID from a combined identifier string.
 * This function assumes the combined string consists of an entity and an ID, separated by a predefined separator.
 * If the string cannot be split (i.e., no separator is found), the original combined string is returned.
 *
 * @param {string} crossId - The combined identifier string.
 * @returns {string} The original ID if the string can be split using the separator, otherwise returns the entire crossId.
 * Example: If crossId is "User_123" and the separator is "_", this returns "123".
 */
export const getRealID = crossId => {
  const splitStr = crossId.split(CROSS_FILTER_ID_SEPARATOR);
  return splitStr.length > 1 ? splitStr[1] : crossId;
};

/**
 * Transforms a list of filters into an object where keys are derived from the filters and their values are arrays
 * of associated IDs. Filters can contain a separator denoting key-value pairs; if no separator is found, the
 * filter itself is added as a value under a default key provided by the entity parameter.
 *
 * @param {string[]} filters - The array of filter strings to be processed.
 * @param {string} entity - The default key to use for filters that do not contain the separator.
 * @returns {object|null} An object mapping keys to arrays of values based on the filters, or null if inputs are invalid.
 */
export const getMappedFilters = (filters, entity) => {
  if (!Array.isArray(filters) || !filters.length || typeof entity !== 'string' || !entity) {
    console.warn("Invalid input: 'filters' must be a non-empty array and 'entity' must be a non-empty string.");
    return null;
  }
  return filters.reduce((obj, filter) => {
    if (typeof filter !== 'string') {
      console.warn(`Skipping invalid filter: expected a string, received ${typeof filter}`);
      return obj;
    }
    const splitFilter = filter.split(CROSS_FILTER_ID_SEPARATOR);
    const {
      key,
      value
    } = splitFilter.length > 1 ? {
      key: splitFilter[0],
      value: splitFilter[1]
    } : {
      key: entity,
      value: filter
    };
    if (!obj[key]) obj[key] = [];
    if (value) {
      obj[key].push(value);
    } else {
      console.warn('Skipping filter due to lack of valid content.');
    }
    return obj;
  }, {});
};